import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, arrayUnion, collection, query, where, getDocs, deleteDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { portalAuth, portalDb, db as sweepDb, checkAndHandleReportStatus, syncUserReportsWithMainApp, notifyReportAdded, addNotificationToUser } from '../utils/firebase';
import { signOut } from 'firebase/auth';
import { toast, Toaster } from 'react-hot-toast';
import { useAuthState } from 'react-firebase-hooks/auth';
import { Plus, Search, Trash2, ExternalLink, Edit, Save, X, CheckCircle, AlertCircle, AlertTriangle, Eye, Share, Copy, User, DollarSign, FileText, PlusCircle, ArrowUp, RotateCw, MessageSquare } from 'lucide-react';
import ReportModal from '../components/ReportModal';
import NotificationCenter from '../components/NotificationCenter';

const DashboardPage = () => {
  const [user, loading] = useAuthState(portalAuth);
  const [userData, setUserData] = useState(null);
  const [savedReports, setSavedReports] = useState([]);
  const [reportDetails, setReportDetails] = useState({});
  const [reportNotes, setReportNotes] = useState({});
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddingReport, setIsAddingReport] = useState(false);
  const [newReportId, setNewReportId] = useState('');
  const [isLoadingReports, setIsLoadingReports] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  const [viewingReportId, setViewingReportId] = useState(null);
  const [newReportsCount, setNewReportsCount] = useState(0);
  const [visibleReportsCount, setVisibleReportsCount] = useState(6);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadMessagesMap, setUnreadMessagesMap] = useState({});
  
  const navigate = useNavigate();

  // Redirect to login if not authenticated
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [loading, navigate, user]);

  // Add resize listener for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Navigate to settings page
  const navigateToSettings = () => {
    navigate('/settings');
  };

  // Set up effect to fetch user data and reports when user logs in
  useEffect(() => {
    // Only run this if we have a user
    if (!user) return;
    
    const fetchUserData = async () => {
      try {
        const userDoc = await getDoc(doc(portalDb, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserData(userData);
          
          // Get saved reports
          const savedReportIds = userData.savedReports || [];
          setSavedReports(savedReportIds);
          
          // Check for new reports since last login
          if (userData.lastLogin && userData.previousReports) {
            const previousReportIds = userData.previousReports;
            const newReports = savedReportIds.filter(id => !previousReportIds.includes(id));
            setNewReportsCount(newReports.length);
          }
          
          // Update previous reports to current state
          await updateDoc(doc(portalDb, 'users', user.uid), {
            previousReports: savedReportIds
          });
          
          // Fetch details for each report
          if (savedReportIds.length > 0) {
            fetchReportDetails(savedReportIds);
            fetchReportNotes(savedReportIds);
          }
          
          // Sync reports with main app to check for removed reports
          syncUserReportsWithMainApp(user.uid)
            .then(result => {
              if (result.missingReports > 0) {
                toast.warning(`${result.missingReports} of your saved reports are no longer available.`);
              }
            })
            .catch(error => {
              console.error('Error syncing reports with main app:', error);
            });
          
          // Set up a real-time listener for the user document to catch updates
          const userRef = doc(portalDb, 'users', user.uid);
          const unsubscribe = onSnapshot(userRef, (docSnapshot) => {
            if (docSnapshot.exists()) {
              const newUserData = docSnapshot.data();
              
              // Check if savedReports has changed
              const newSavedReports = newUserData.savedReports || [];
              const currentSavedReports = savedReports; // capture current value for comparison
              
              if (JSON.stringify(newSavedReports) !== JSON.stringify(currentSavedReports)) {
                console.log('Saved reports changed, updating state...');
                
                // If we have removed reports, update the displayed reports
                const removedReports = currentSavedReports.filter(id => !newSavedReports.includes(id));
                if (removedReports.length > 0) {
                  // Remove the details of these reports from local state
                  const newReportDetails = { ...reportDetails };
                  removedReports.forEach(id => {
                    delete newReportDetails[id];
                  });
                  setReportDetails(newReportDetails);
                }
                
                // If we have new reports, fetch their details
                const addedReports = newSavedReports.filter(id => !currentSavedReports.includes(id));
                if (addedReports.length > 0) {
                  fetchReportDetails(addedReports);
                  fetchReportNotes(addedReports);
                }
                
                // Update savedReports state
                setSavedReports(newSavedReports);
              }
              
              // Update other user data if needed
              setUserData(newUserData);
            }
          });
          
          // Clean up the listener on unmount
          return () => unsubscribe();
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        toast.error('Error loading your profile');
      }
    };
    
    fetchUserData();
    
    // Cleanup function to remove all listeners when component unmounts
    return () => {
      // Clean up any existing real-time listeners
      if (window.reportListeners) {
        window.reportListeners.forEach(unsubscribe => unsubscribe());
        window.reportListeners = [];
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Add a function to set up real-time listeners for each report
  const setupReportListeners = (reportIds) => {
    // Clear any existing listeners
    if (window.reportListeners) {
      window.reportListeners.forEach(unsubscribe => unsubscribe());
    }
    
    // Create a new array to store listeners
    window.reportListeners = [];
    
    // Set up a listener for each report
    reportIds.forEach(reportId => {
      const reportRef = doc(sweepDb, 'inspections', reportId);
      const unsubscribe = onSnapshot(reportRef, 
        // onNext callback - when document changes
        (docSnapshot) => {
          if (docSnapshot.exists()) {
            const reportData = docSnapshot.data();
            console.log(`Real-time update for report ${reportId}:`, reportData);
            
            // If the report status has changed, update it in the UI
            setReportDetails(prevDetails => {
              // Get the current report data to preserve metadata if needed
              const currentReport = prevDetails[reportId];
              
              // If report was deleted or is now unavailable, mark it as error but preserve metadata
              if (reportData.status === 'removed' || reportData.status === 'trashed') {
                // Create savedMetadata from the current report data if it exists and isn't already an error
                const savedMetadata = currentReport && !currentReport.error 
                  ? {
                    dateOfInspection: currentReport.dateOfInspection,
                    createdAt: currentReport.createdAt,
                    updatedAt: currentReport.updatedAt,
                    clientName: currentReport.clientName,
                    propertyAddress: currentReport.propertyAddress || currentReport.address
                  } 
                  : null;
                
                return {
                  ...prevDetails,
                  [reportId]: { 
                    error: true, 
                    errorType: 'deleted',
                    savedMetadata // Include the saved metadata
                  }
                };
              }
              
              // Otherwise update with latest data
              return {
                ...prevDetails,
                [reportId]: {
                  ...reportData,
                  isPaid: reportData.isPaid !== false // Ensure isPaid is set correctly
                }
              };
            });
          } else {
            // Document doesn't exist anymore
            console.log(`Report ${reportId} no longer exists`);
            setReportDetails(prevDetails => {
              // Get the current report data to preserve metadata
              const currentReport = prevDetails[reportId];
              
              // Create savedMetadata from the current report data if it exists and isn't already an error
              const savedMetadata = currentReport && !currentReport.error 
                ? {
                  dateOfInspection: currentReport.dateOfInspection,
                  createdAt: currentReport.createdAt,
                  updatedAt: currentReport.updatedAt,
                  clientName: currentReport.clientName,
                  propertyAddress: currentReport.propertyAddress || currentReport.address
                } 
                : null;
              
              return {
                ...prevDetails,
                [reportId]: { 
                  error: true, 
                  errorType: 'not_found',
                  savedMetadata // Include the saved metadata
                }
              };
            });
          }
        },
        // onError callback
        (error) => {
          console.error(`Error in real-time listener for report ${reportId}:`, error);
        }
      );
      
      // Store the unsubscribe function
      window.reportListeners.push(unsubscribe);
    });
    
    // Return a cleanup function
    return () => {
      if (window.reportListeners) {
        window.reportListeners.forEach(unsubscribe => unsubscribe());
        window.reportListeners = [];
      }
    };
  };

  // Modify fetchReportDetails to set up listeners after loading initial data
  const fetchReportDetails = async (reportIds) => {
    setIsLoadingReports(true);
    const reportDetailsObj = {};
    
    try {
      const promises = reportIds.map(async (reportId) => {
        try {
          // Use the new status checking function
          const reportStatus = await checkAndHandleReportStatus(reportId, user?.uid);
          
          if (reportStatus.status === 'active') {
            // Report is active, use the data
            const reportData = reportStatus.reportData;
            
            // Preserve isPaid status (if it exists in the report)
            if (reportStatus.isPaid !== undefined) {
              reportData.isPaid = reportStatus.isPaid;
            }
            
            reportDetailsObj[reportId] = reportData;
          } 
          else {
            // Report doesn't exist or can't be accessed
            // Check if we already have metadata for this report in our local state
            const existingReport = reportDetails[reportId];
            const savedMetadata = existingReport && !existingReport.error 
              ? {
                dateOfInspection: existingReport.dateOfInspection,
                createdAt: existingReport.createdAt,
                updatedAt: existingReport.updatedAt,
                clientName: existingReport.clientName,
                propertyAddress: existingReport.propertyAddress || existingReport.address
              } 
              : null;
              
            reportDetailsObj[reportId] = { 
              error: true, 
              errorType: reportStatus.status,
              savedMetadata // Include the saved metadata if available
            };
          }
        } catch (error) {
          console.error(`Error fetching report ${reportId}:`, error);
          
          // Check if we already have metadata for this report in our local state
          const existingReport = reportDetails[reportId];
          const savedMetadata = existingReport && !existingReport.error 
            ? {
              dateOfInspection: existingReport.dateOfInspection,
              createdAt: existingReport.createdAt,
              updatedAt: existingReport.updatedAt,
              clientName: existingReport.clientName,
              propertyAddress: existingReport.propertyAddress || existingReport.address
            } 
            : null;
            
          reportDetailsObj[reportId] = { 
            error: true, 
            errorType: 'unknown', 
            errorMessage: error.message,
            savedMetadata // Include the saved metadata if available
          };
        }
      });
      
      await Promise.all(promises);
      
      // Update report details state
      setReportDetails(prevDetails => ({
        ...prevDetails,
        ...reportDetailsObj
      }));
      
      // Set up real-time listeners for these reports
      setupReportListeners(reportIds);
      
    } catch (error) {
      console.error('Error fetching report details:', error);
      toast.error('Error loading your reports');
    } finally {
      setIsLoadingReports(false);
    }
  };

  // Fetch notes for each report
  const fetchReportNotes = async (reportIds) => {
    if (!user) return;
    
    setIsLoadingNotes(true);
    const notesObj = {};
    
    try {
      // Query for all report notes for this user
      const q = query(
        collection(portalDb, 'reportNotes'),
        where('userId', '==', user.uid)
      );
      
      const querySnapshot = await getDocs(q);
      
      querySnapshot.forEach((doc) => {
        const noteData = doc.data();
        if (reportIds.includes(noteData.reportId)) {
          notesObj[noteData.reportId] = {
            id: doc.id,
            ...noteData
          };
        }
      });
      
      setReportNotes(notesObj);
    } catch (error) {
      console.error('Error fetching report notes:', error);
      toast.error('Error loading notes');
    } finally {
      setIsLoadingNotes(false);
    }
  };

  // Handle editing a note
  const handleEditNote = (reportId) => {
    // If there's an existing note for this report, set it for editing
    if (reportNotes[reportId]) {
      setNoteText(reportNotes[reportId].notes);
    } else {
      setNoteText('');
    }
    setEditingNoteId(reportId);
  };

  // Handle saving a note
  const handleSaveNote = async (reportId) => {
    if (!user) return;
    
    try {
      const noteData = {
        userId: user.uid,
        reportId: reportId,
        notes: noteText,
        updatedAt: new Date().toISOString()
      };
      
      // Check if this report already has a note
      if (reportNotes[reportId] && reportNotes[reportId].id) {
        // Update existing note
        await setDoc(
          doc(portalDb, 'reportNotes', reportNotes[reportId].id),
          {
            ...noteData,
            createdAt: reportNotes[reportId].createdAt || new Date().toISOString()
          }
        );
        
        // Update local state
        setReportNotes(prev => ({
          ...prev,
          [reportId]: {
            ...prev[reportId],
            notes: noteText,
            updatedAt: noteData.updatedAt
          }
        }));
      } else {
        // Create new note
        const fullNoteData = {
          ...noteData,
          createdAt: new Date().toISOString()
        };
        
        await setDoc(
          doc(portalDb, 'reportNotes', `${user.uid}_${reportId}`),
          fullNoteData
        );
        
        // Update local state
        setReportNotes(prev => ({
          ...prev,
          [reportId]: {
            id: `${user.uid}_${reportId}`,
            ...fullNoteData
          }
        }));
      }
      
      setEditingNoteId(null);
      toast.success('Note saved');
    } catch (error) {
      console.error('Error saving note:', error);
      toast.error('Error saving note');
    }
  };

  // Cancel editing a note
  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setNoteText('');
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await signOut(portalAuth);
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error('Error signing out');
    }
  };

  // Add a new report manually
  const handleAddReport = async () => {
    if (!newReportId.trim()) {
      toast.error('Please enter a report ID');
      return;
    }
    
    // Clean up the ID (in case they pasted a URL)
    let cleanId = newReportId.trim();
    
    // If they pasted a full URL, extract just the ID
    if (cleanId.includes('/share-report/')) {
      cleanId = cleanId.split('/share-report/')[1];
    }
    
    // Remove any trailing slashes or query params
    cleanId = cleanId.split('?')[0].split('#')[0].replace(/\/$/, '');
    
    // Check if report already exists in user's saved reports
    if (savedReports.includes(cleanId)) {
      toast.error('This report is already in your collection');
      return;
    }
    
    try {
      // Verify the report exists in the main database
      const reportDoc = await getDoc(doc(sweepDb, 'inspections', cleanId));
      
      if (!reportDoc.exists()) {
        toast.error('Report not found. Please check the ID and try again.');
        return;
      }
      
      // Add the report ID to the user's saved reports
      await updateDoc(doc(portalDb, 'users', user.uid), {
        savedReports: arrayUnion(cleanId)
      });
      
      // Update local state
      const updatedReports = [...savedReports, cleanId];
      setSavedReports(updatedReports);
      
      // Fetch the report details
      const reportData = reportDoc.data();
      setReportDetails(prev => ({
        ...prev,
        [cleanId]: reportData
      }));
      
      // Manually add notification about the new report
      // Only called if the report is truly newly added
      await notifyReportAdded(user.uid, cleanId, reportData);
      
      toast.success('Report added successfully!');
      setNewReportId('');
      setIsAddingReport(false);
    } catch (error) {
      console.error('Error adding report:', error);
      toast.error('Error adding report');
    }
  };

  // Remove a report from saved reports
  const handleRemoveReport = async (reportId) => {
    try {
      // Get current saved reports
      const userDoc = await getDoc(doc(portalDb, 'users', user.uid));
      const currentReports = userDoc.data().savedReports || [];
      
      // Get report details for the notification before removing
      const reportDoc = await getDoc(doc(sweepDb, 'inspections', reportId));
      let reportData = { propertyAddress: 'Unknown Property' };
      if (reportDoc.exists()) {
        reportData = reportDoc.data();
      } else if (reportDetails[reportId]) {
        // Fallback to local state if report not found in database
        reportData = reportDetails[reportId];
      }
      
      // Filter out the report to remove
      const updatedReports = currentReports.filter(id => id !== reportId);
      
      // Update in Firestore
      await updateDoc(doc(portalDb, 'users', user.uid), {
        savedReports: updatedReports
      });
      
      // Update local state
      setSavedReports(updatedReports);
      
      // Remove from report details
      const newReportDetails = { ...reportDetails };
      delete newReportDetails[reportId];
      setReportDetails(newReportDetails);
      
      // Delete any associated notes
      if (reportNotes[reportId] && reportNotes[reportId].id) {
        await deleteDoc(doc(portalDb, 'reportNotes', reportNotes[reportId].id));
        
        // Update local state for notes
        const newReportNotes = { ...reportNotes };
        delete newReportNotes[reportId];
        setReportNotes(newReportNotes);
      }
      
      // Create a notification that the report was removed
      await addNotificationToUser(user.uid, {
        type: 'report_removed',
        title: 'Report Removed',
        message: `The report for "${reportData.propertyAddress}" has been removed from your account.`,
        reportId: reportId,
        propertyAddress: reportData.propertyAddress,
        clientName: reportData.clientName || 'Unknown Client'
      });
      
      toast.success('Report removed');
    } catch (error) {
      console.error('Error removing report:', error);
      toast.error('Error removing report');
    }
  };

  // Open a report in a new tab
  const openReport = (reportId) => {
    // URL includes the source parameter to indicate the user is coming from the portal
    window.open(`https://oiltank.app/share-report/${reportId}?source=portal`, '_blank');
  };

  // Filter reports based on search query
  const filteredReports = savedReports.filter(reportId => {
    const report = reportDetails[reportId];
    if (!report) return false;
    
    const searchLower = searchQuery.toLowerCase();
    
    // Search in address, client name, or report ID
    return (
      reportId.toLowerCase().includes(searchLower) ||
      (report.address && report.address.toLowerCase().includes(searchLower)) ||
      (report.clientName && report.clientName.toLowerCase().includes(searchLower))
    );
  }).sort((reportIdA, reportIdB) => {
    // Get the reports
    const reportA = reportDetails[reportIdA];
    const reportB = reportDetails[reportIdB];
    
    // Skip sorting if either report is missing
    if (!reportA || !reportB) return 0;
    
    // Parse dates (handle Firestore timestamp objects as well as date strings)
    let dateA = null;
    let dateB = null;
    
    try {
      // For reportA - Check if this is an error report with savedMetadata
      if (reportA.error && reportA.savedMetadata && reportA.savedMetadata.dateOfInspection) {
        // Use savedMetadata for deleted reports
        dateA = typeof reportA.savedMetadata.dateOfInspection === 'object' && reportA.savedMetadata.dateOfInspection.seconds
          ? new Date(reportA.savedMetadata.dateOfInspection.seconds * 1000)
          : new Date(reportA.savedMetadata.dateOfInspection);
      }
      // Otherwise use regular date
      else if (reportA.dateOfInspection) {
        dateA = typeof reportA.dateOfInspection === 'object' && reportA.dateOfInspection.seconds
          ? new Date(reportA.dateOfInspection.seconds * 1000)
          : new Date(reportA.dateOfInspection);
      }
      
      // For reportB - Check if this is an error report with savedMetadata
      if (reportB.error && reportB.savedMetadata && reportB.savedMetadata.dateOfInspection) {
        // Use savedMetadata for deleted reports
        dateB = typeof reportB.savedMetadata.dateOfInspection === 'object' && reportB.savedMetadata.dateOfInspection.seconds
          ? new Date(reportB.savedMetadata.dateOfInspection.seconds * 1000)
          : new Date(reportB.savedMetadata.dateOfInspection);
      }
      // Otherwise use regular date
      else if (reportB.dateOfInspection) {
        dateB = typeof reportB.dateOfInspection === 'object' && reportB.dateOfInspection.seconds
          ? new Date(reportB.dateOfInspection.seconds * 1000)
          : new Date(reportB.dateOfInspection);
      }
      
      // If we can't parse either date, use createdAt as fallback
      // For reportA - First try savedMetadata if it's an error report
      if (!dateA) {
        if (reportA.error && reportA.savedMetadata && reportA.savedMetadata.createdAt) {
          dateA = typeof reportA.savedMetadata.createdAt === 'object' && reportA.savedMetadata.createdAt.seconds
            ? new Date(reportA.savedMetadata.createdAt.seconds * 1000)
            : new Date(reportA.savedMetadata.createdAt);
        }
        // Then try regular createdAt
        else if (reportA.createdAt) {
          dateA = typeof reportA.createdAt === 'object' && reportA.createdAt.seconds
            ? new Date(reportA.createdAt.seconds * 1000)
            : new Date(reportA.createdAt);
        }
      }
      
      // For reportB - First try savedMetadata if it's an error report
      if (!dateB) {
        if (reportB.error && reportB.savedMetadata && reportB.savedMetadata.createdAt) {
          dateB = typeof reportB.savedMetadata.createdAt === 'object' && reportB.savedMetadata.createdAt.seconds
            ? new Date(reportB.savedMetadata.createdAt.seconds * 1000)
            : new Date(reportB.savedMetadata.createdAt);
        }
        // Then try regular createdAt
        else if (reportB.createdAt) {
          dateB = typeof reportB.createdAt === 'object' && reportB.createdAt.seconds
            ? new Date(reportB.createdAt.seconds * 1000)
            : new Date(reportB.createdAt);
        }
      }
      
      // Sort by date (newest first)
      if (dateA && dateB) {
        const dateDiff = dateB - dateA; // Descending order (newest first)
        
        // If dates are equal, use a secondary sort criteria
        if (dateDiff === 0) {
          // Try using updatedAt or createdAt timestamp for more precision
          const timeA = reportA.error && reportA.savedMetadata ? 
            (reportA.savedMetadata.updatedAt || reportA.savedMetadata.createdAt) :
            (reportA.updatedAt || reportA.createdAt);
            
          const timeB = reportB.error && reportB.savedMetadata ? 
            (reportB.savedMetadata.updatedAt || reportB.savedMetadata.createdAt) :
            (reportB.updatedAt || reportB.createdAt);
          
          if (timeA && timeB) {
            const timeADate = typeof timeA === 'object' && timeA.seconds
              ? new Date(timeA.seconds * 1000)
              : new Date(timeA);
              
            const timeBDate = typeof timeB === 'object' && timeB.seconds
              ? new Date(timeB.seconds * 1000)
              : new Date(timeB);
              
            const timeDiff = timeBDate - timeADate;
            if (timeDiff !== 0) return timeDiff;
          }
          
          // If timestamps are also equal or not available, use reportId as final tiebreaker
          // This ensures a consistent, deterministic order
          return reportIdA.localeCompare(reportIdB);
        }
        
        return dateDiff;
      }
    } catch (error) {
      console.error('Error sorting reports by date:', error);
    }
    
    // Default: sort by reportId as fallback
    return reportIdA.localeCompare(reportIdB);
  });

  // Helper function to format address by removing USA
  const formatAddress = (address) => {
    if (!address) return 'Address not available';
    return address
      .replace(/,\s*USA$/i, '')
      .replace(/\s*USA$/i, '');
  };

  // Helper function to get scenario icon and color
  const getScenarioIndicator = (scenario) => {
    switch(scenario) {
      case 'noTank':
        return {
          icon: <CheckCircle size={18} className="text-green-500" />,
          label: 'No tank detected',
          color: 'text-green-500',
          badgeClasses: 'bg-green-100 text-green-800'
        };
      case 'anomaly':
        return {
          icon: <AlertCircle size={18} className="text-amber-500" />,
          label: 'Metallic anomaly detected',
          color: 'text-amber-500',
          badgeClasses: 'bg-amber-100 text-amber-800'
        };
      case 'ustFound':
        return {
          icon: <AlertTriangle size={18} className="text-red-500" />,
          label: 'Underground tank identified',
          color: 'text-red-500',
          badgeClasses: 'bg-red-100 text-red-800'
        };
      default:
        return {
          icon: <AlertCircle size={18} className="text-gray-400" />,
          label: 'Unknown status',
          color: 'text-gray-400',
          badgeClasses: 'bg-gray-100 text-gray-800'
        };
    }
  };

  // Open the report modal
  const handleViewReport = (reportId) => {
    setViewingReportId(reportId);
    
    // When opening a report via the modal, this is a good place to reset the unread message count
    // This doesn't affect the database - that will happen in the ReportModal component
    // This just updates the UI immediately for better user experience
    if (unreadMessagesMap[reportId] && unreadMessagesMap[reportId] > 0) {
      setUnreadMessagesMap(prev => ({
        ...prev,
        [reportId]: 0  // Reset the count for this report
      }));
    }
  };
  
  // Close the report modal
  const handleCloseModal = () => {
    setViewingReportId(null);
  };

  // Now add a new function to handle sharing the report
  const handleShareReport = async (reportId) => {
    const shareUrl = `https://oiltank.app/share-report/${reportId}`;
    
    try {
      if (navigator.share) {
        // Use Web Share API if available (mobile devices)
        await navigator.share({
          title: 'Oil Tank Sweep Report',
          text: 'Check out this Oil Tank Sweep Report:',
          url: shareUrl
        });
        toast.success('Shared successfully');
      } else {
        // Otherwise copy to clipboard
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied to clipboard!');
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error sharing report:', error);
        toast.error('Error sharing report');
      }
    }
  };

  // Add a function to handle copying the link
  const handleCopyLink = async (reportId) => {
    try {
      const shareUrl = `https://oiltank.app/share-report/${reportId}`;
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard!');
    } catch (error) {
      console.error('Error copying link:', error);
      toast.error('Failed to copy link');
    }
  };

  // Function to scroll to top for mobile action bar
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Function to refresh the page for mobile action bar
  const refreshPage = () => {
    window.location.reload();
  };

  // Add a function to fetch unread messages for all reports
  useEffect(() => {
    if (!user || savedReports.length === 0) return;
    
    // Create a query for messages where the recipient is not the sender and read is false
    const messagesRef = collection(portalDb, 'reportMessages');
    
    // Listen for changes to messages for user's reports
    const q = query(
      messagesRef,
      where('reportId', 'in', savedReports.slice(0, 10))  // Limit to 10 reports at a time for query performance
    );
    
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const newUnreadMessagesMap = {};
      
      querySnapshot.forEach((doc) => {
        const message = doc.data();
        // Only count messages not from the current user and that are unread
        if (message.senderId !== user.uid && !message.read) {
          if (!newUnreadMessagesMap[message.reportId]) {
            newUnreadMessagesMap[message.reportId] = 1;
          } else {
            newUnreadMessagesMap[message.reportId]++;
          }
        }
      });
      
      setUnreadMessagesMap(newUnreadMessagesMap);
    });
    
    return () => unsubscribe();
  }, [user, savedReports]);

  // For larger report lists, add listeners in batches for the remaining reports
  useEffect(() => {
    if (!user || savedReports.length <= 10) return;
    
    const batchSize = 10;
    const unsubscribers = [];
    
    // Process reports in batches of 10 (Firebase limit for 'in' query)
    for (let i = 10; i < savedReports.length; i += batchSize) {
      const batch = savedReports.slice(i, i + batchSize);
      
      const messagesRef = collection(portalDb, 'reportMessages');
      const q = query(messagesRef, where('reportId', 'in', batch));
      
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        setUnreadMessagesMap(prev => {
          const updated = { ...prev };
          
          querySnapshot.forEach((doc) => {
            const message = doc.data();
            if (message.senderId !== user.uid && !message.read) {
              if (!updated[message.reportId]) {
                updated[message.reportId] = 1;
              } else {
                updated[message.reportId]++;
              }
            }
          });
          
          return updated;
        });
      });
      
      unsubscribers.push(unsubscribe);
    }
    
    return () => {
      unsubscribers.forEach(unsubscribe => unsubscribe());
    };
  }, [user, savedReports]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-12">
      <Toaster position="top-center" />
      
      {/* Fixed Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-sm border-b border-gray-200 shadow-sm z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-12">
            <div className="flex items-center">
              <div className="h-8 w-8 bg-gradient-to-tr from-blue-500 to-cyan-400 rounded-full flex items-center justify-center shadow-sm">
                <img 
                  src="/oil.png" 
                  alt="Oil Tank Sweep" 
                  className="h-5 w-5 brightness-0 invert" 
                />
              </div>
              <h1 className="text-xl font-semibold ml-3 bg-gradient-to-r from-blue-500 to-cyan-400 bg-clip-text text-transparent">OilTank.App</h1>
            </div>
            
            <div className="flex items-center space-x-4">
              <NotificationCenter />
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="h-5 w-24 bg-gray-200 rounded animate-pulse"></div>
                </div>
              ) : userData && (
                <div className="flex items-center gap-2 text-slate-600">
                  <button 
                    className="flex items-center gap-2 text-sm hover:text-blue-500 transition-colors cursor-pointer" 
                    title="Account Settings"
                    onClick={navigateToSettings}
                  >
                    {isMobile ? (
                      <User className="h-5 w-5 text-blue-500" />
                    ) : (
                      <>
                        {userData.firstName} {userData.lastName ? userData.lastName.charAt(0) + '.' : ''}
                      </>
                    )}
                  </button>
                </div>
              )}
              <div className="h-4 w-px bg-slate-200" />
              <button
                onClick={handleLogout}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-slate-700 bg-slate-100 hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {loading || !userData ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-pulse flex flex-col items-center">
              <div className="h-8 bg-gray-200 rounded w-56 mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-72 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-64"></div>
            </div>
          </div>
        ) : (
          <>
            {/* Welcome Banner */}
            <div className="bg-gradient-to-r from-blue-500 to-cyan-400 shadow-md rounded-lg p-6 mb-8 text-white max-w-5xl mx-auto">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-2xl font-bold">Welcome back, {userData.firstName}!</h2>
                  <p className="mt-2 max-w-3xl">
                    Your dashboard gives you access to all your oil tank sweep reports. Whether you're managing properties 
                    for yourself or your clients, you can easily organize, annotate, and share all reports from one place.
                  </p>
                  <div className="mt-4 flex items-center space-x-3" style={{ position: 'relative', zIndex: 0 }}>
                    <div className="px-3 py-1 bg-white/30 rounded-md">
                      <span className="font-semibold">{savedReports.length}</span> {savedReports.length === 1 ? 'Report' : 'Reports'} Available
                    </div>
                    
                    {newReportsCount > 0 && (
                      <div className="px-3 py-1 bg-white/30 rounded-md border-l-4 border-yellow-400">
                        <span className="font-semibold">{newReportsCount} new</span> {newReportsCount === 1 ? 'report' : 'reports'} since your last visit!
                      </div>
                    )}
                  </div>
                </div>
                <div className="hidden md:block">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-white/80" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
              </div>
            </div>

            {/* User Info Section */}
            <div className="bg-white shadow-sm rounded-lg p-6 mb-8 max-w-5xl mx-auto">
              <h2 className="text-xl font-bold mb-4 bg-gradient-to-r from-blue-500 to-cyan-400 bg-clip-text text-transparent">Account Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Name</h3>
                  <p className="mt-1 text-lg font-semibold text-gray-900">
                    {userData.firstName} {userData.lastName}
                  </p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Role</h3>
                  <p className="mt-1 text-lg font-semibold text-gray-900">
                    {userData.userType === 'realtor' ? 'Realtor' : 
                    userData.userType === 'attorney' ? 'Attorney' : 
                    userData.userType === 'client' ? 'Client' : 'Other'}
                  </p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Company</h3>
                  <p className="mt-1 text-lg font-semibold text-gray-900">
                    {userData.companyName || 'Not specified'}
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
        
        {/* Reports Section */}
        {!loading && userData && (
          <div className="bg-white shadow-sm rounded-lg p-6 max-w-5xl mx-auto">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900">My Tank Sweep Reports</h2>
                <p className="text-sm text-gray-600 mt-1">
                  <span className="sm:hidden">Manage your reports</span>
                  <span className="hidden sm:inline">View, manage, and share tank sweep reports with clients and colleagues</span>
                </p>
              </div>
              
              <button
                onClick={() => setIsAddingReport(true)}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-500 hover:bg-blue-600"
              >
                <Plus size={16} className="mr-1" />
                <span className="hidden xs:inline">Add Report</span>
                <span className="xs:hidden">Add</span>
              </button>
            </div>
            
            {/* Search */}
            <div className="mb-6">
              <div className="flex gap-2 items-center border border-gray-300 rounded-md px-3 py-2 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                <Search size={18} className="text-gray-400 flex-shrink-0" />
                <input
                  type="text"
                  placeholder="Search reports..."
                  className="flex-1 focus:outline-none text-sm sm:text-base w-full"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1 hidden sm:block">Find reports quickly by typing an address, name, or report identifier</p>
            </div>
            
            {/* Add Report Form */}
            {isAddingReport && (
              <div className="mb-6 p-4 border border-gray-200 rounded-md bg-gray-50">
                <h3 className="text-lg font-medium mb-3">Add a New Report</h3>
                <div className="flex items-center">
                  <input
                    type="text"
                    placeholder="Enter Report ID or report URL..."
                    value={newReportId}
                    onChange={(e) => setNewReportId(e.target.value)}
                    className="flex-1 focus:ring-blue-500 focus:border-blue-500 block w-full px-3 py-2 sm:text-sm border border-gray-300 rounded-md"
                  />
                  <button
                    onClick={handleAddReport}
                    className="ml-3 inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-500 hover:bg-blue-600"
                  >
                    Add
                  </button>
                  <button
                    onClick={() => setIsAddingReport(false)}
                    className="ml-2 inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  You can enter a Report ID or paste the full report URL (e.g., oiltank.app/share-report/ABC123)
                </p>
              </div>
            )}
            
            {/* Reports List */}
            {isLoadingReports ? (
              <div className="text-center py-12 bg-gray-50 rounded-md">
                <svg className="animate-spin h-8 w-8 text-blue-500 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p className="text-gray-600">Loading your reports...</p>
              </div>
            ) : savedReports.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-md">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-gray-600 mb-2">You don't have any saved reports yet.</p>
                <p className="text-gray-500 text-sm mb-4">Add your first report to get started and keep track of your property inspections.</p>
                <button
                  onClick={() => setIsAddingReport(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-500 hover:bg-blue-600"
                >
                  <Plus size={16} className="mr-1" />
                  Add Your First Report
                </button>
              </div>
            ) : filteredReports.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-md">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <p className="text-gray-600 mb-2">No reports match your search</p>
                <p className="text-gray-500 text-sm">Try using different keywords or clear your search</p>
              </div>
            ) : (
              <>
                {/* Reports Available section - ensure this has appropriate styling and position */}
                <div className="flex justify-between items-center mb-4">
                  <div className="font-medium text-gray-700">
                    <span className="text-sm">{filteredReports.length} {filteredReports.length === 1 ? 'Report' : 'Reports'} Found</span>
                  </div>
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
                    >
                      <X className="h-3 w-3 mr-1" />
                      Clear Search
                    </button>
                  )}
                </div>
                
                <div className="space-y-4">
                  {filteredReports.slice(0, visibleReportsCount).map(reportId => {
                    const report = reportDetails[reportId];
                    const hasError = report?.error;
                    
                    // If report failed to load, show improved error card
                    if (hasError) {
                      return (
                        <div key={reportId} className="border border-gray-200 rounded-lg bg-red-50 shadow-sm hover:shadow-md transition-all duration-200">
                          {/* Top section with report info - not clickable for error reports */}
                          <div className="p-4 border-b border-gray-100">
                            <div className="flex justify-between items-start">
                              <div className="flex-1 select-none">
                                <h3 className="text-lg font-medium text-gray-800 flex items-center">
                                  <span className="line-through mr-2">
                                    {report.savedMetadata?.clientName || 'Report Reference'}
                                  </span>
                                  <span className="text-sm bg-red-100 text-red-800 px-2 py-1 rounded">Not Available</span>
                                </h3>
                                <p className="text-sm text-gray-600">Report ID: {reportId.substring(0, 8)}...</p>
                                {report.savedMetadata?.propertyAddress && (
                                  <p className="text-sm text-gray-600 line-through">
                                    {formatAddress(report.savedMetadata.propertyAddress)}
                                  </p>
                                )}
                                <p className="text-xs text-gray-600 mt-1">
                                  This report is no longer available in the system. It may have been permanently deleted by the inspector.
                                </p>
                              </div>
                              <button
                                onClick={() => handleRemoveReport(reportId)}
                                className="text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 p-2 rounded-full transition-colors"
                                title="Remove this broken reference from your account"
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                          </div>
                          
                          {/* Notes Section - same structure as regular reports */}
                          <div className="p-4 pt-3">
                            <div className="flex justify-between items-center mb-2">
                              {reportNotes[reportId]?.notes ? (
                                <div className="flex justify-between items-center w-full">
                                  <h4 className="text-sm font-medium text-gray-700">Notes</h4>
                                  {editingNoteId !== reportId && (
                                    <div className="flex items-center space-x-3">
                                      <button
                                        onClick={() => handleEditNote(reportId)}
                                        className="text-xs text-blue-500 hover:text-blue-700 flex items-center"
                                      >
                                        <Edit size={14} className="mr-1" />
                                        Edit
                                      </button>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="flex justify-between items-center w-full">
                                  {editingNoteId !== reportId && (
                                    <div className="flex justify-between items-center w-full">
                                      <button
                                        onClick={() => handleEditNote(reportId)}
                                        className="text-xs text-blue-500 hover:text-blue-700 flex items-center"
                                      >
                                        <PlusCircle size={14} className="mr-1" />
                                        Add Notes
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Notes editing and display */}
                            {editingNoteId === reportId ? (
                              <div>
                                <textarea
                                  value={noteText}
                                  onChange={(e) => setNoteText(e.target.value)}
                                  placeholder="Add your notes about this report here..."
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  rows={3}
                                />
                                <div className="flex justify-end space-x-2 mt-2">
                                  <button
                                    onClick={handleCancelEdit}
                                    className="text-xs flex items-center text-gray-600 hover:text-gray-800"
                                  >
                                    <X size={14} className="mr-1" />
                                    Cancel
                                  </button>
                                  <button
                                    onClick={() => handleSaveNote(reportId)}
                                    className="text-xs flex items-center text-blue-500 hover:text-blue-700"
                                  >
                                    <Save size={14} className="mr-1" />
                                    Save Notes
                                  </button>
                                </div>
                              </div>
                            ) : reportNotes[reportId]?.notes ? (
                              <div className="text-sm text-gray-600 bg-white p-3 rounded border border-gray-100">
                                <div>
                                  {reportNotes[reportId].notes.split('\n').map((line, i) => (
                                    <div key={i}>{line || <br />}</div>
                                  ))}
                                  <div className="text-xs text-gray-400 mt-2">
                                    Last updated: {new Date(reportNotes[reportId].updatedAt).toLocaleString()}
                                  </div>
                                </div>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      );
                    }
                    
                    // Format dates
                    let inspectionDate = 'N/A';
                    if (report?.dateOfInspection) {
                      try {
                        // Handle different date formats
                        const dateObj = typeof report.dateOfInspection === 'object' 
                          ? new Date(report.dateOfInspection.seconds * 1000) 
                          : new Date(report.dateOfInspection);
                          
                        inspectionDate = dateObj.toLocaleDateString();
                      } catch (e) {
                        console.error('Error formatting date:', e);
                      }
                    }
                    
                    // Check if the report is marked as unpaid
                    const isUnpaid = report?.isPaid === false;

                    return (
                      <div key={reportId} className={`border ${isUnpaid ? 'border-amber-200' : 'border-gray-200'} rounded-lg ${isUnpaid ? 'bg-amber-50' : 'bg-gray-50'} shadow-sm hover:shadow-md transition-all duration-200`}>
                        {/* Make the entire top section clickable for non-unpaid reports */}
                        {!isUnpaid ? (
                          <div 
                            className="p-4 cursor-pointer group border-b border-gray-100 hover:bg-white transition-colors duration-200" 
                            onClick={() => handleViewReport(reportId)}
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <h3 className="text-lg font-medium text-gray-800 mr-2 group-hover:text-blue-600 transition-colors duration-200">{report?.clientName || 'Unnamed Report'}</h3>
                                <p className="text-sm text-gray-600 group-hover:text-blue-600 transition-colors duration-200">{formatAddress(report?.propertyAddress || report?.address)}</p>
                                <p className="text-xs text-gray-500 mt-1">Inspection Date: {inspectionDate}</p>
                                
                                {/* Group badges in a flex container for proper alignment */}
                                <div className="flex flex-wrap items-center mt-2">
                                  {/* Show scenario badges if available */}
                                  {report?.scenario && (
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getScenarioIndicator(report.scenario).badgeClasses}`}>
                                      {getScenarioIndicator(report.scenario).icon}
                                      <span className="ml-1">{getScenarioIndicator(report.scenario).label}</span>
                                    </span>
                                  )}
                                  
                                  {/* Show resource count badge if available */}
                                  {report?.resources?.length > 0 && (
                                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                      <FileText className="h-3 w-3 mr-1" />
                                      {report.resources.length === 1 ? '1 Resource' : `${report.resources.length} Resources`}
                                    </span>
                                  )}
                                  
                                  {/* Show unread messages badge if there are any */}
                                  {unreadMessagesMap[reportId] && unreadMessagesMap[reportId] > 0 && (
                                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                      <MessageSquare className="h-3 w-3 mr-1" />
                                      {unreadMessagesMap[reportId]} unread {unreadMessagesMap[reportId] === 1 ? 'message' : 'messages'}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="p-4 border-b border-gray-100">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <h3 className="text-lg font-medium text-gray-800 mr-2">{report?.clientName || 'Unnamed Report'}</h3>
                                <p className="text-sm text-gray-600">{formatAddress(report?.propertyAddress || report?.address)}</p>
                                <p className="text-xs text-gray-500 mt-1">Inspection Date: {inspectionDate}</p>
                                
                                {/* Group badges in a flex container for proper alignment */}
                                <div className="flex flex-wrap items-center mt-2">
                                  {/* Show scenario badges if available */}
                                  {report?.scenario && (
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getScenarioIndicator(report.scenario).badgeClasses}`}>
                                      {getScenarioIndicator(report.scenario).icon}
                                      <span className="ml-1">{getScenarioIndicator(report.scenario).label}</span>
                                    </span>
                                  )}
                                  
                                  {/* Show resource count badge if available */}
                                  {report?.resources?.length > 0 && (
                                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                      <FileText className="h-3 w-3 mr-1" />
                                      {report.resources.length === 1 ? '1 Resource' : `${report.resources.length} Resources`}
                                    </span>
                                  )}
                                  
                                  {/* Show unread messages badge if there are any */}
                                  {unreadMessagesMap[reportId] && unreadMessagesMap[reportId] > 0 && (
                                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                      <MessageSquare className="h-3 w-3 mr-1" />
                                      {unreadMessagesMap[reportId]} unread {unreadMessagesMap[reportId] === 1 ? 'message' : 'messages'}
                                    </span>
                                  )}
                                </div>
                                
                                {/* Show payment badge for unpaid reports */}
                                <span className="mt-2 inline-block bg-amber-100 text-amber-800 text-xs px-2 py-1 rounded flex items-center" title="Payment required">
                                  <DollarSign size={12} className="mr-1" />Payment Required
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Payment warning for unpaid reports - outside the clickable area */}
                        {isUnpaid && (
                          <div className="px-4 pb-3 pt-0">
                            <div className="bg-amber-100 p-2 rounded text-xs text-amber-800 flex items-start">
                              <AlertCircle size={14} className="mr-1 flex-shrink-0 mt-0.5" />
                              <p>This report requires payment. Contact the inspector to arrange payment and gain full access.</p>
                            </div>
                          </div>
                        )}
                        
                        {/* Notes Section - outside the clickable area */}
                        <div className="p-4 pt-3">
                          <div className="flex justify-between items-center mb-2">
                            {reportNotes[reportId]?.notes ? (
                              <div className="flex justify-between items-center w-full">
                                <h4 className="text-sm font-medium text-gray-700">Notes</h4>
                                {editingNoteId !== reportId && (
                                  <div className="flex items-center space-x-3">
                                    <button
                                      onClick={() => handleEditNote(reportId)}
                                      className="text-xs text-blue-500 hover:text-blue-700 flex items-center"
                                    >
                                      <Edit size={14} className="mr-1" />
                                      Edit
                                    </button>

                                    {/* Action buttons for mobile */}
                                    <div className="md:hidden relative">
                                      <button
                                        onClick={() => {
                                          const dropdown = document.getElementById(`dropdown-${reportId}`);
                                          if (dropdown) dropdown.classList.toggle('hidden');
                                        }}
                                        className="text-xs text-gray-500 hover:text-gray-700 flex items-center"
                                      >
                                        Actions ▼
                                      </button>
                                      <div id={`dropdown-${reportId}`} className="hidden absolute right-0 mt-2 py-1 w-36 bg-white shadow-lg rounded-md z-10">
                                        {!isUnpaid && (
                                          <>
                                            <button onClick={() => handleViewReport(reportId)} className="flex items-center w-full px-4 py-2 text-xs text-left text-gray-700 hover:bg-gray-100">
                                              <Eye size={14} className="mr-2" />
                                              View
                                              {unreadMessagesMap[reportId] && unreadMessagesMap[reportId] > 0 && (
                                                <span className="ml-auto bg-red-500 text-white text-xs rounded-full h-4 min-w-[16px] flex items-center justify-center">
                                                  {unreadMessagesMap[reportId]}
                                                </span>
                                              )}
                                            </button>
                                            <button onClick={() => openReport(reportId)} className="flex items-center w-full px-4 py-2 text-xs text-left text-gray-700 hover:bg-gray-100">
                                              <ExternalLink size={14} className="mr-2" />
                                              Open
                                            </button>
                                          </>
                                        )}
                                        <button onClick={() => handleCopyLink(reportId)} className="flex items-center w-full px-4 py-2 text-xs text-left text-gray-700 hover:bg-gray-100">
                                          <Copy size={14} className="mr-2" />
                                          Copy Link
                                        </button>
                                        <button onClick={() => handleShareReport(reportId)} className="flex items-center w-full px-4 py-2 text-xs text-left text-gray-700 hover:bg-gray-100">
                                          <Share size={14} className="mr-2" />
                                          Share
                                        </button>
                                        <button onClick={() => handleRemoveReport(reportId)} className="flex items-center w-full px-4 py-2 text-xs text-left text-gray-700 hover:bg-gray-100">
                                          <Trash2 size={14} className="mr-2" />
                                          Remove
                                        </button>
                                      </div>
                                    </div>

                                    {/* Action buttons for desktop */}
                                    <div className="hidden md:flex items-center space-x-1">
                                      {!isUnpaid && (
                                        <>
                                          <button 
                                            onClick={() => handleViewReport(reportId)} 
                                            className="p-1 text-gray-500 hover:text-blue-600 relative" 
                                            title="View report details"
                                          >
                                            <Eye size={14} />
                                            {unreadMessagesMap[reportId] && unreadMessagesMap[reportId] > 0 && (
                                              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-3 w-3 flex items-center justify-center"></span>
                                            )}
                                          </button>
                                          <button onClick={() => openReport(reportId)} className="p-1 text-gray-500 hover:text-blue-600" title="Open full report">
                                            <ExternalLink size={14} />
                                          </button>
                                        </>
                                      )}
                                      <button onClick={() => handleCopyLink(reportId)} className="p-1 text-gray-500 hover:text-purple-600" title="Copy link">
                                        <Copy size={14} />
                                      </button>
                                      <button onClick={() => handleShareReport(reportId)} className="p-1 text-gray-500 hover:text-green-600" title="Share report">
                                        <Share size={14} />
                                      </button>
                                      <button onClick={() => handleRemoveReport(reportId)} className="p-1 text-gray-500 hover:text-red-600" title="Remove report">
                                        <Trash2 size={14} />
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="flex justify-between items-center w-full">
                                {editingNoteId !== reportId && (
                                  <div className="flex justify-between items-center w-full">
                                    <button
                                      onClick={() => handleEditNote(reportId)}
                                      className="text-xs text-blue-500 hover:text-blue-700 flex items-center"
                                    >
                                      <PlusCircle size={14} className="mr-1" />
                                      Add Notes
                                    </button>
                                    
                                    <div className="flex items-center space-x-3">
                                      {/* Action buttons for mobile */}
                                      <div className="md:hidden relative">
                                        <button
                                          onClick={() => {
                                            const dropdown = document.getElementById(`dropdown-${reportId}`);
                                            if (dropdown) dropdown.classList.toggle('hidden');
                                          }}
                                          className="text-xs text-gray-500 hover:text-gray-700 flex items-center"
                                        >
                                          Actions ▼
                                        </button>
                                        <div id={`dropdown-${reportId}`} className="hidden absolute right-0 mt-2 py-1 w-36 bg-white shadow-lg rounded-md z-10">
                                          {!isUnpaid && (
                                            <>
                                              <button onClick={() => handleViewReport(reportId)} className="flex items-center w-full px-4 py-2 text-xs text-left text-gray-700 hover:bg-gray-100">
                                                <Eye size={14} className="mr-2" />
                                                View
                                                {unreadMessagesMap[reportId] && unreadMessagesMap[reportId] > 0 && (
                                                  <span className="ml-auto bg-red-500 text-white text-xs rounded-full h-4 min-w-[16px] flex items-center justify-center">
                                                    {unreadMessagesMap[reportId]}
                                                  </span>
                                                )}
                                              </button>
                                              <button onClick={() => openReport(reportId)} className="flex items-center w-full px-4 py-2 text-xs text-left text-gray-700 hover:bg-gray-100">
                                                <ExternalLink size={14} className="mr-2" />
                                                Open
                                              </button>
                                            </>
                                          )}
                                          <button onClick={() => handleCopyLink(reportId)} className="flex items-center w-full px-4 py-2 text-xs text-left text-gray-700 hover:bg-gray-100">
                                            <Copy size={14} className="mr-2" />
                                            Copy Link
                                          </button>
                                          <button onClick={() => handleShareReport(reportId)} className="flex items-center w-full px-4 py-2 text-xs text-left text-gray-700 hover:bg-gray-100">
                                            <Share size={14} className="mr-2" />
                                            Share
                                          </button>
                                          <button onClick={() => handleRemoveReport(reportId)} className="flex items-center w-full px-4 py-2 text-xs text-left text-gray-700 hover:bg-gray-100">
                                            <Trash2 size={14} className="mr-2" />
                                            Remove
                                          </button>
                                        </div>
                                      </div>

                                      {/* Action buttons for desktop */}
                                      <div className="hidden md:flex items-center space-x-1">
                                        {!isUnpaid && (
                                          <>
                                            <button 
                                              onClick={() => handleViewReport(reportId)} 
                                              className="p-1 text-gray-500 hover:text-blue-600 relative" 
                                              title="View report details"
                                            >
                                              <Eye size={14} />
                                              {unreadMessagesMap[reportId] && unreadMessagesMap[reportId] > 0 && (
                                                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-3 w-3 flex items-center justify-center"></span>
                                              )}
                                            </button>
                                            <button onClick={() => openReport(reportId)} className="p-1 text-gray-500 hover:text-blue-600" title="Open full report">
                                              <ExternalLink size={14} />
                                            </button>
                                          </>
                                        )}
                                        <button onClick={() => handleCopyLink(reportId)} className="p-1 text-gray-500 hover:text-purple-600" title="Copy link">
                                          <Copy size={14} />
                                        </button>
                                        <button onClick={() => handleShareReport(reportId)} className="p-1 text-gray-500 hover:text-green-600" title="Share report">
                                          <Share size={14} />
                                        </button>
                                        <button onClick={() => handleRemoveReport(reportId)} className="p-1 text-gray-500 hover:text-red-600" title="Remove report">
                                          <Trash2 size={14} />
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Notes editing and display */}
                          {editingNoteId === reportId ? (
                            <div>
                              <textarea
                                value={noteText}
                                onChange={(e) => setNoteText(e.target.value)}
                                placeholder="Add your notes about this report here..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                rows={3}
                              />
                              <div className="flex justify-end space-x-2 mt-2">
                                <button
                                  onClick={handleCancelEdit}
                                  className="text-xs flex items-center text-gray-600 hover:text-gray-800"
                                >
                                  <X size={14} className="mr-1" />
                                  Cancel
                                </button>
                                <button
                                  onClick={() => handleSaveNote(reportId)}
                                  className="text-xs flex items-center text-blue-500 hover:text-blue-700"
                                >
                                  <Save size={14} className="mr-1" />
                                  Save Notes
                                </button>
                              </div>
                            </div>
                          ) : reportNotes[reportId]?.notes ? (
                            <div className="text-sm text-gray-600 bg-white p-3 rounded border border-gray-100">
                              <div>
                                {reportNotes[reportId].notes.split('\n').map((line, i) => (
                                  <div key={i}>{line || <br />}</div>
                                ))}
                                <div className="text-xs text-gray-400 mt-2">
                                  Last updated: {new Date(reportNotes[reportId].updatedAt).toLocaleString()}
                                </div>
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {visibleReportsCount < filteredReports.length && (
                  <div className="mt-8 text-center">
                    <button
                      onClick={() => setVisibleReportsCount(prev => prev + 6)}
                      className="px-4 py-2 bg-gray-100 rounded-md text-gray-700 hover:bg-gray-200 transition-colors inline-flex items-center"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                      Load More Reports
                    </button>
                    <p className="text-xs text-gray-500 mt-2">
                      Showing {Math.min(visibleReportsCount, filteredReports.length)} of {filteredReports.length} reports
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </main>
      
      {/* Footer with help information */}
      <footer className="bg-white border-t border-gray-200 mt-12">
        <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wider">About Your Reports</h3>
              <p className="mt-4 text-sm text-gray-500">
                Oil tank sweep reports provide detailed information about potential underground storage tanks on properties.
                Whether you're a property owner, realtor, or attorney, you can add notes, track details, and share information with relevant parties.
              </p>
            </div>
            
            <div>
              <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wider">Report Features</h3>
              <ul className="mt-4 space-y-3">
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mt-0.5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-sm text-gray-500">Add private notes for transaction details or follow-ups</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mt-0.5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-sm text-gray-500">Share reports with clients, colleagues, and other parties</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mt-0.5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-sm text-gray-500">Centralize property documentation for transactions</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wider">Need Help?</h3>
              <p className="mt-4 text-sm text-gray-500">
                If you need assistance with your reports or have questions about your account, please contact our support team.
              </p>
              <div className="mt-4">
                <a href="mailto:leo@makeasmartchoice.us" className="inline-flex items-center text-sm font-medium text-blue-500 hover:text-blue-600">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  leo@makeasmartchoice.us
                </a>
              </div>
            </div>
          </div>
          
          <div className="mt-8 pt-8 border-t border-gray-200">
            <p className="text-center text-sm text-gray-500">
              &copy; {new Date().getFullYear()} OilTank.App. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
      
      {/* Mobile Action Bar */}
      {isMobile && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50">
          <div className="flex gap-4 p-2 rounded-full bg-white/80 backdrop-blur-md shadow-lg border border-slate-200">
            <button
              onClick={scrollToTop}
              className="p-3 rounded-full bg-blue-500 text-white hover:bg-blue-600 transition-colors active:transform active:scale-95"
              aria-label="Scroll to top"
            >
              <ArrowUp className="h-5 w-5" />
            </button>
            <button
              onClick={refreshPage}
              className="p-3 rounded-full bg-blue-500 text-white hover:bg-blue-600 transition-colors active:transform active:scale-95"
              aria-label="Refresh page"
            >
              <RotateCw className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
      
      {/* Report Modal */}
      {viewingReportId && (
        <ReportModal 
          reportId={viewingReportId}
          onClose={handleCloseModal}
          userData={userData}
          currentUser={user}
        />
      )}
    </div>
  );
};

export default DashboardPage; 